import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, HStack } from "@chakra-ui/react";
import PersonChip from "./PersonChip";
import { fetchTasksBySource } from "../../services/messagingService";
import { openMessages } from "../../utils/messagingBus";
import { onWorkItemsChanged } from "../../utils/workItemsBus";
import { normalizeStatus, STATUS_META } from "../../utils/workItemStatus";

/**
 * Things that can be assigned as a Messages task — a Live Activity row, a
 * signed order on a chart — and then show that task. The task points back with
 * internal_tasks.source_kind + source_ref.
 */

// A row shows its newest task that hasn't been cancelled. { source_ref: task }
export function latestTaskBySourceRef(tasks) {
  const byRef = {};
  for (const t of tasks || []) {
    if (!t.source_ref || t.status === "cancelled") continue;
    const prev = byRef[t.source_ref];
    if (!prev || String(t.created_at || "") > String(prev.created_at || "")) byRef[t.source_ref] = t;
  }
  return byRef;
}

// The tasks made from just these sources (say one chart's signed orders):
// { taskByRef, reloadTasks }. Nothing is asked for while there are no refs.
// Reloaded whenever a task changes anywhere. `label` names the place in
// console warnings.
export function useTasksForRefs(sourceKind, refs, label) {
  const [taskByRef, setTaskByRef] = useState({});
  const refsKey = (refs || []).join("\n");
  const reloadTasks = useCallback(async () => {
    const list = refsKey ? refsKey.split("\n") : [];
    if (list.length === 0) {
      setTaskByRef({});
      return;
    }
    try {
      const { tasks } = await fetchTasksBySource(sourceKind, { refs: list });
      setTaskByRef(latestTaskBySourceRef(tasks));
    } catch (e) {
      console.warn(`${label}: could not load their tasks`, e);
    }
  }, [sourceKind, refsKey, label]);
  useEffect(() => { reloadTasks(); }, [reloadTasks]);
  useEffect(() => onWorkItemsChanged(() => reloadTasks()), [reloadTasks]);
  return { taskByRef, reloadTasks };
}

// "Assigned to (A) Amy [STUDENT] [Assigned] Open in Tasks" under a row that has a task.
export function SourceTaskChip({ task, testId }) {
  const meta = STATUS_META[normalizeStatus(task.status)] || STATUS_META.assigned;
  return (
    <HStack spacing={2} mt={1} fontSize="xs" data-testid={testId} flexWrap="wrap">
      <PersonChip label="Assigned to" name={task.assigned_to_name || "someone"} schema={task.assigned_to_schema}
                  size="xs" avatarSize="2xs" />
      <Badge colorScheme={meta.colorScheme} borderRadius="full" px={2}>{meta.label}</Badge>
      <Button size="xs" variant="link" colorScheme="blue"
              onClick={() => openMessages({ tab: "tasks", focus: { kind: "task", id: task.id } })}>
        Open in Tasks
      </Button>
    </HStack>
  );
}
